import { useMemo, useState, useEffect } from 'react';
import { normalizeJobDetail, type JobDetailNormalizationResult } from '../../../../../utils/data/normalize-job-detail';
import { transformJobForLanguage } from '../utils/transformJob';
import type { DisplayJob } from '../../../../../types';
import type { FinalJob } from '../../../../../lambda/types';

export interface JobNormalizationResult {
  displayJob: DisplayJob | null;
  finalJob: FinalJob | null;
  normalizationMeta: JobDetailNormalizationResult | null;
}

export interface UseJobNormalizationProps {
  job: FinalJob | null | undefined;
  jobLocale: string;
  seoSlug?: string;
}

/**
 * Hook to normalize job data for display
 * Handles language transformation and normalization
 */
export function useJobNormalization({
  job,
  jobLocale,
  seoSlug,
}: UseJobNormalizationProps): JobNormalizationResult {
  // Compute normalized job data synchronously from SSR data to avoid skeleton flash
  const normalizedJobData = useMemo(() => {
    if (!job) {
      return { displayJob: null, finalJob: null, normalizationMeta: null };
    }

    const transformedJob = transformJobForLanguage(job, jobLocale) as DisplayJob;
    const normalizationResult = normalizeJobDetail(job, transformedJob, {
      locale: jobLocale,
      seoSlug,
    });

    return {
      displayJob: normalizationResult.displayJob,
      finalJob: normalizationResult.canonicalJob,
      normalizationMeta: normalizationResult,
    };
  }, [job, jobLocale, seoSlug]);

  // Initialize state from computed normalized data (available immediately from SSR)
  const [displayJob, setDisplayJob] = useState<DisplayJob | null | undefined>(
    normalizedJobData.displayJob ?? undefined
  );
  const [finalJob, setFinalJob] = useState<FinalJob | null>(normalizedJobData.finalJob);
  const [normalizationMeta, setNormalizationMeta] = useState<JobDetailNormalizationResult | null>(
    normalizedJobData.normalizationMeta
  );

  // Update state when normalized data changes
  useEffect(() => {
    if (normalizedJobData.displayJob !== undefined) {
      setDisplayJob(normalizedJobData.displayJob);
    }
    if (normalizedJobData.finalJob !== undefined) {
      setFinalJob(normalizedJobData.finalJob);
    }
    if (normalizedJobData.normalizationMeta !== undefined) {
      setNormalizationMeta(normalizedJobData.normalizationMeta);
    }
  }, [normalizedJobData]);

  return {
    displayJob: displayJob ?? null,
    finalJob,
    normalizationMeta,
  };
}
